import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/primitives";
import { API } from "../lib/site";
import { ASSESSMENT_AREAS } from "../lib/plans";
import { HOTEL_TOKEN_KEY } from "./Join";

const SCALE = [
    [1, "Not started"],
    [2, "Exploring"],
    [3, "Developing"],
    [4, "Established"],
    [5, "Leading"],
];

const HotelAssessment = () => {
    const [scores, setScores] = useState({});
    const [status, setStatus] = useState("idle");
    const [overall, setOverall] = useState(null);
    const navigate = useNavigate();

    const token = localStorage.getItem(HOTEL_TOKEN_KEY);

    const submit = async (e) => {
        e.preventDefault();
        if (Object.keys(scores).length < ASSESSMENT_AREAS.length) {
            toast.error("Please rate all eight areas before submitting.");
            return;
        }
        setStatus("submitting");
        try {
            const { data } = await axios.post(
                `${API}/hotels/assessment`,
                { scores },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setOverall(data.overall);
            setStatus("done");
        } catch (err) {
            setStatus("idle");
            if (err.response?.status === 401) {
                localStorage.removeItem(HOTEL_TOKEN_KEY);
                navigate("/hotel/login");
                return;
            }
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Could not submit the assessment.");
        }
    };

    if (!token) {
        return <Navigate to="/hotel/login" replace />;
    }

    return (
        <main>
            <SEO title="GGH Sustainability Assessment | Global Green Hotel" description="Complete your GGH hotel sustainability assessment." />
            <div className="grain min-h-screen bg-[#FDFBF7] py-28">
                <div className="mx-auto max-w-[900px] px-5 sm:px-8">
                    <Link to="/hotel/dashboard" data-testid="assessment-back" className="inline-flex items-center gap-2 text-sm font-bold text-[#1A3B2B] hover:text-[#A4803F]">
                        <ArrowLeft size={15} aria-hidden="true" /> Back to Dashboard
                    </Link>

                    {status === "done" ? (
                        <Reveal>
                            <div data-testid="assessment-result" className="mt-10 rounded-2xl border border-[#1A3B2B]/20 bg-white p-10 text-center shadow-[0_20px_60px_rgba(10,23,17,0.07)]">
                                <p className="eyebrow-green">Your GGH Sustainability Score</p>
                                <p className="font-display mt-4 text-7xl font-medium text-[#1A3B2B]">{overall}%</p>
                                <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-[#4A5D54]">
                                    Your improvement plan with recommended actions is ready on your
                                    dashboard. Revisit the assessment over time to track your
                                    progress.
                                </p>
                                <Link to="/hotel/dashboard" data-testid="assessment-goto-dashboard" className="btn-green mt-8">
                                    View My Dashboard
                                </Link>
                            </div>
                        </Reveal>
                    ) : (
                        <>
                            <Reveal>
                                <p className="eyebrow-green mt-8">GGH Sustainability Assessment</p>
                                <h1 className="font-display mt-4 text-3xl font-medium tracking-tight text-[#0A1711] sm:text-4xl">
                                    Rate your hotel honestly — this is your baseline
                                </h1>
                                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#4A5D54] md:text-base">
                                    For each area, choose the level that best describes your hotel
                                    today: 1 = not started, 3 = developing, 5 = leading. Your score
                                    and improvement plan are generated from your answers.
                                </p>
                            </Reveal>
                            <form onSubmit={submit} data-testid="assessment-form" className="mt-12 grid gap-5">
                                {ASSESSMENT_AREAS.map((area, i) => (
                                    <Reveal key={area.key} delay={0.04 * i} y={18}>
                                        <fieldset
                                            data-testid={`assessment-area-${area.key}`}
                                            className="rounded-2xl border border-[#E8E6E1] bg-white p-6"
                                        >
                                            <legend className="sr-only">{area.label}</legend>
                                            <div className="flex flex-wrap items-start justify-between gap-4">
                                                <div className="max-w-md">
                                                    <p className="text-sm font-extrabold uppercase tracking-wider text-[#0A1711]">
                                                        {i + 1}. {area.label}
                                                    </p>
                                                    <p className="mt-1 text-xs text-[#4A5D54]">{area.desc}</p>
                                                </div>
                                                <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={area.label}>
                                                    {SCALE.map(([value, label]) => (
                                                        <button
                                                            key={value}
                                                            type="button"
                                                            role="radio"
                                                            aria-checked={scores[area.key] === value}
                                                            data-testid={`assessment-${area.key}-${value}`}
                                                            onClick={() => setScores((s) => ({ ...s, [area.key]: value }))}
                                                            title={label}
                                                            className={`flex h-11 w-11 items-center justify-center rounded-full border text-sm font-extrabold transition-[background-color,border-color,transform] duration-200 hover:scale-105 ${
                                                                scores[area.key] === value
                                                                    ? "border-[#1A3B2B] bg-[#1A3B2B] text-[#D9B96A]"
                                                                    : "border-[#E8E6E1] bg-white text-[#4A5D54] hover:border-[#C2A160]"
                                                            }`}
                                                        >
                                                            {value}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </fieldset>
                                    </Reveal>
                                ))}
                                <div className="mt-4">
                                    <button
                                        type="submit"
                                        data-testid="assessment-submit"
                                        disabled={status === "submitting"}
                                        className="btn-green disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {status === "submitting" ? "Calculating…" : "Complete Assessment"}
                                    </button>
                                </div>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </main>
    );
};

export default HotelAssessment;
